import React, { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { logger } from '../log';
import styles from './Auth.module.css';

const log = logger('auth');

interface CustomSignInProps {
  onSignUpClick: () => void;
}

type OAuthProvider = 'oauth_google' | 'oauth_github' | 'oauth_apple';

export const CustomSignIn: React.FC<CustomSignInProps> = ({ onSignUpClick }) => {
  const { signIn, setActive } = useSignIn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Second factor verification state
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false);
  const [secondFactorStrategy, setSecondFactorStrategy] = useState<'email_code' | 'phone_code' | 'totp' | null>(null);
  const [verificationCode, setVerificationCode] = useState('');

  // Forgot password flow state
  const [resetCode, setResetCode] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    if (!signIn) return;

    try {
      log.info(`starting OAuth sign-in with: ${provider}`);
      await signIn.authenticateWithRedirect({
        strategy: provider,
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/`,
      });
    } catch (err: any) {
      log.error('OAuth sign-in error', err);
      setError(err.errors?.[0]?.longMessage || `Failed to sign in with ${provider}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;

    setLoading(true);
    setError('');

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
      } else if (result.status === 'needs_second_factor') {
        // Client Trust or MFA triggered - need second factor verification
        log.info('second factor required', { supportedStrategies: result.supportedSecondFactors });

        // Determine which strategy to use (prefer totp, then email_code, then phone_code)
        const supportedStrategies = result.supportedSecondFactors?.map(f => f.strategy) || [];
        if (supportedStrategies.includes('totp')) {
          setSecondFactorStrategy('totp');
          // TOTP doesn't need preparation — user has their authenticator app
        } else if (supportedStrategies.includes('email_code')) {
          setSecondFactorStrategy('email_code');
          await signIn.prepareSecondFactor({ strategy: 'email_code' });
        } else if (supportedStrategies.includes('phone_code')) {
          setSecondFactorStrategy('phone_code');
          await signIn.prepareSecondFactor({ strategy: 'phone_code' });
        } else {
          // Fallback: try email_code anyway (Clerk's default for Client Trust)
          setSecondFactorStrategy('email_code');
          await signIn.prepareSecondFactor({ strategy: 'email_code' });
        }

        setNeedsSecondFactor(true);
        setLoading(false);
      }
    } catch (err: any) {
      const errorCode = err.errors?.[0]?.code;
      if (errorCode === 'form_password_compromised') {
        log.warn('password compromised detected, prompting alternative sign-in');
        setError(
          'Your password may be compromised. Please sign in with Google or GitHub above, then you will be prompted to set a new password.'
        );
      } else {
        setError(err.errors?.[0]?.longMessage || 'Invalid email or password');
      }
      setLoading(false);
    }
  };

  const handleSecondFactorVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn || !secondFactorStrategy) return;

    setLoading(true);
    setError('');

    try {
      const result = await signIn.attemptSecondFactor({
        strategy: secondFactorStrategy,
        code: verificationCode,
      });

      if (result.status === 'complete') {
        log.info('second factor verification successful');
        await setActive({ session: result.createdSessionId });
      }
    } catch (err: any) {
      log.error('second factor verification failed', err);
      setError(err.errors?.[0]?.longMessage || 'Invalid verification code');
      setLoading(false);
    }
  };

  const handleResendSecondFactorCode = async () => {
    if (!signIn || !secondFactorStrategy) return;

    setLoading(true);
    setError('');

    try {
      await signIn.prepareSecondFactor({ strategy: secondFactorStrategy });
      log.info('second factor code resent');
    } catch (err: any) {
      log.error('failed to resend second factor code', err);
      setError(err.errors?.[0]?.longMessage || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;

    setLoading(true);
    setError('');

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: resetEmail,
      });
      setResetSent(true);
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetCodeVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;

    setLoading(true);
    setError('');

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: resetCode,
      });

      if (result.status === 'needs_new_password') {
        setNeedsNewPassword(true);
        setLoading(false);
      } else if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Invalid verification code');
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await signIn.resetPassword({
        password: newPassword,
        signOutOfOtherSessions: true,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Failed to set new password');
      setLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    if (!signIn) return;

    setLoading(true);
    setError('');

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: resetEmail,
      });
      log.info('password reset code resent');
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // Second factor verification screen
  if (needsSecondFactor) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.eyebrow}>Interlucent</div>
            <h1 className={styles.title}>VERIFY YOUR IDENTITY</h1>
            <p className={styles.subtitle}>
              {secondFactorStrategy === 'totp'
                ? 'Enter the code from your authenticator app'
                : secondFactorStrategy === 'email_code'
                  ? "We've sent a verification code to your email address"
                  : "We've sent a verification code to your phone"}
            </p>
          </div>

          <form onSubmit={handleSecondFactorVerify} className={styles.form}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.field}>
              <label className={styles.label}>VERIFICATION CODE</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={styles.input}
                placeholder="Enter 6-digit code"
                required
                autoFocus
                autoComplete="one-time-code"
              />
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length < 6}
              className={styles.button}
            >
              {loading ? 'VERIFYING...' : 'VERIFY'}
            </button>

            {secondFactorStrategy !== 'totp' && (
              <div className={styles.footer}>
                <span>Didn't receive a code?</span>
                <button
                  type="button"
                  onClick={handleResendSecondFactorCode}
                  disabled={loading}
                  className={styles.linkButton}
                >
                  Resend code
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setNeedsSecondFactor(false);
                setVerificationCode('');
                setSecondFactorStrategy(null);
                setError('');
              }}
              className={styles.linkButton}
            >
              Back to Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    const resetBackToSignIn = () => {
      setShowForgotPassword(false);
      setResetSent(false);
      setResetEmail('');
      setResetCode('');
      setNeedsNewPassword(false);
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    };

    // Step 3: Set new password
    if (needsNewPassword) {
      return (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.eyebrow}>Interlucent</div>
              <h1 className={styles.title}>SET NEW PASSWORD</h1>
              <p className={styles.subtitle}>
                Choose a new password for your account
              </p>
            </div>

            <form onSubmit={handleNewPasswordSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.field}>
                <label className={styles.label}>NEW PASSWORD</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={styles.input}
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={8}
                />
                <p className={styles.hint}>Minimum 8 characters</p>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>CONFIRM PASSWORD</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.input}
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                disabled={loading || newPassword.length < 8}
                className={styles.button}
              >
                {loading ? 'SETTING PASSWORD...' : 'SET NEW PASSWORD'}
              </button>

              <button
                type="button"
                onClick={resetBackToSignIn}
                className={styles.linkButton}
              >
                Back to Sign In
              </button>
            </form>
          </div>
        </div>
      );
    }

    // Step 2: Enter verification code
    if (resetSent) {
      return (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.eyebrow}>Interlucent</div>
              <h1 className={styles.title}>VERIFY CODE</h1>
              <p className={styles.subtitle}>
                Enter the verification code sent to {resetEmail}
              </p>
            </div>

            <form onSubmit={handleResetCodeVerify} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.field}>
                <label className={styles.label}>VERIFICATION CODE</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className={styles.input}
                  placeholder="Enter 6-digit code"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                disabled={loading || resetCode.length < 6}
                className={styles.button}
              >
                {loading ? 'VERIFYING...' : 'VERIFY CODE'}
              </button>

              <div className={styles.footer}>
                <span>Didn't receive a code?</span>
                <button
                  type="button"
                  onClick={handleResendResetCode}
                  disabled={loading}
                  className={styles.linkButton}
                >
                  Resend code
                </button>
              </div>

              <button
                type="button"
                onClick={resetBackToSignIn}
                className={styles.linkButton}
              >
                Back to Sign In
              </button>
            </form>
          </div>
        </div>
      );
    }

    // Step 1: Enter email
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.eyebrow}>Interlucent</div>
            <h1 className={styles.title}>RESET PASSWORD</h1>
            <p className={styles.subtitle}>
              Enter your email address and we'll send you a verification code
            </p>
          </div>

          <form onSubmit={handleForgotPassword} className={styles.form}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.field}>
              <label className={styles.label}>EMAIL ADDRESS</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className={styles.input}
                required
                autoFocus
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={styles.button}
            >
              {loading ? 'SENDING...' : 'SEND RESET CODE'}
            </button>

            <button
              type="button"
              onClick={resetBackToSignIn}
              className={styles.linkButton}
            >
              Back to Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.eyebrow}>Interlucent</div>
          <h1 className={styles.title}>SIGN IN</h1>
          <p className={styles.subtitle}>
            Welcome back to Interlucent Console
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          {/* SSO Buttons */}
          <div className={styles.ssoButtons}>
            <button
              type="button"
              onClick={() => handleOAuthSignIn('oauth_google')}
              className={styles.ssoButton}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              <span>Continue with Google</span>
            </button>

            <button
              type="button"
              onClick={() => handleOAuthSignIn('oauth_github')}
              className={styles.ssoButton}
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
              </svg>
              <span>Continue with GitHub</span>
            </button>

            {/* Apple SSO - disabled until Apple Developer account is configured
            <button
              type="button"
              onClick={() => handleOAuthSignIn('oauth_apple')}
              className={styles.ssoButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span>Continue with Apple</span>
            </button>
            */}
          </div>

          <div className={styles.divider}>
            <span>OR</span>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>EMAIL ADDRESS</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
              autoFocus
              autoComplete="email"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className={styles.linkButton}
          >
            Forgot password?
          </button>

          <button
            type="submit"
            disabled={loading}
            className={styles.button}
          >
            {loading ? 'SIGNING IN...' : 'SIGN IN'}
          </button>

          <div className={styles.footer}>
            <span>Don't have an account?</span>
            <button
              type="button"
              onClick={onSignUpClick}
              className={styles.linkButton}
            >
              Sign up
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
